import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./cookie-consent-banner.m.css";

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [adsConsent, setAdsConsent] = useState(false);
  const [mounted, setMounted] = useState(false);

  const pageType = useSelector((state) => state.qt?.pageType);
  const isLoginPage = pageType === "user-login";

  useEffect(() => {
    setMounted(true);
    // Check if user has already made a selection
    const consent = getConsentCookie();
    if (!consent) {
      // Show banner if no consent cookie is found
      setVisible(true);
    } else {
      setAnalyticsConsent(consent.analytics);
      setAdsConsent(consent.ads);
    }

    const handleOpenBanner = () => {
      const currentConsent = getConsentCookie() || { analytics: false, ads: false };
      setAnalyticsConsent(currentConsent.analytics);
      setAdsConsent(currentConsent.ads);
      setVisible(true);
    };

    window.addEventListener("openCookieConsent", handleOpenBanner);
    return () => {
      window.removeEventListener("openCookieConsent", handleOpenBanner);
    };
  }, []);

  const getConsentCookie = () => {
    if (typeof document === "undefined") return null;
    const name = "cookie_consent=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i].trim();
      if (c.indexOf(name) === 0) {
        try {
          return JSON.parse(c.substring(name.length));
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  };

  const setConsentCookie = (consentObj) => {
    const d = new Date();
    d.setTime(d.getTime() + 365 * 24 * 60 * 60 * 1000); // 365 days
    const expires = "expires=" + d.toUTCString();
    document.cookie = `cookie_consent=${JSON.stringify(consentObj)};${expires};path=/;SameSite=Lax`;
  };

  const updateConsentMode = (consentObj) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: consentObj.analytics ? "granted" : "denied",
        ad_storage: consentObj.ads ? "granted" : "denied",
        ad_user_data: consentObj.ads ? "granted" : "denied",
        ad_personalization: consentObj.ads ? "granted" : "denied",
      });
    }
  };

  const clearNonEssentialCookies = () => {
    if (typeof document === "undefined") return;
    const cookiesToClear = ["_ga", "_gid", "_gat", "_ga_BG2BCPXF3R", "_ga_472233442"];
    const domains = [window.location.hostname, "." + window.location.hostname];
    
    // Attempt to extract the base domain
    const hostParts = window.location.hostname.split(".");
    if (hostParts.length >= 2) {
      const baseDomain = "." + hostParts.slice(-2).join(".");
      domains.push(baseDomain);
    }

    cookiesToClear.forEach((cookieName) => {
      domains.forEach((domain) => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
      });
    });
  };

  const handleAcceptAll = () => {
    const consent = { analytics: true, ads: true };
    setAnalyticsConsent(true);
    setAdsConsent(true);
    setConsentCookie(consent);
    updateConsentMode(consent);
    setVisible(false);
  };

  const handleRejectAll = () => {
    const consent = { analytics: false, ads: false };
    setAnalyticsConsent(false);
    setAdsConsent(false);
    setConsentCookie(consent);
    updateConsentMode(consent);
    clearNonEssentialCookies();
    setVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = { analytics: analyticsConsent, ads: adsConsent };
    setConsentCookie(consent);
    updateConsentMode(consent);
    if (!analyticsConsent) {
      clearNonEssentialCookies();
    }
    setVisible(false);
  };

  if (!mounted) return null;

  return (
    <>
      {visible && (
        <div styleName="consent-overlay">
          <div styleName="consent-banner">
            <h2 styleName="consent-title">Evästeasetukset & Tietosuoja</h2>
            <p styleName="consent-text">
              Käytämme evästeitä parantaaksemme sivuston käyttökokemusta ja analysoidaksemme liikennettä. Valitsemalla 'Hyväksy kaikki' annat suostumuksesi kaikille evästeille. Voit myös muokata asetuksia tai kieltää valinnaiset evästeet.
            </p>

            <hr styleName="divider" />

            <div styleName="preferences-container">
              <div styleName="preference-item">
                <div styleName="preference-info">
                  <span styleName="preference-label">Välttämättömät evästeet</span>
                  <span styleName="preference-desc">
                    Tarvitaan sivuston perustoimintoihin, kuten kirjautumiseen ja asetusten tallentamiseen. Näitä ei voi poistaa käytöstä.
                  </span>
                </div>
                <div styleName="checkbox-disabled">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              </div>

              <div styleName="preference-item">
                <div styleName="preference-info">
                  <span styleName="preference-label">Analytiikkaevästeet</span>
                  <span styleName="preference-desc">
                    Auttavat meitä ymmärtämään, miten kävijät käyttävät sivustoa, jotta voimme parantaa palvelua.
                  </span>
                </div>
                <label styleName="switch">
                  <input
                    type="checkbox"
                    checked={analyticsConsent}
                    onChange={(e) => setAnalyticsConsent(e.target.checked)}
                  />
                  <span styleName="slider"></span>
                </label>
              </div>

              <div styleName="preference-item">
                <div styleName="preference-info">
                  <span styleName="preference-label">Markkinointi- ja mainosevästeet</span>
                  <span styleName="preference-desc">
                    Käytetään mainonnan kohdentamiseen ja sen tehokkuuden mittaamiseen.
                  </span>
                </div>
                <label styleName="switch">
                  <input
                    type="checkbox"
                    checked={adsConsent}
                    onChange={(e) => setAdsConsent(e.target.checked)}
                  />
                  <span styleName="slider"></span>
                </label>
              </div>
            </div>

            <div styleName="buttons-container">
              <button onClick={handleSavePreferences} styleName="btn-red">Tallenna valinnat</button>
              <button onClick={handleAcceptAll} styleName="btn-black">Hyväksy kaikki</button>
              <button onClick={handleRejectAll} styleName="btn-black">Hylkää kaikki</button>
            </div>
          </div>
        </div>
      )}

      {!visible && isLoginPage && (
        <button
          onClick={() => {
            const consent = getConsentCookie() || { analytics: false, ads: false };
            setAnalyticsConsent(consent.analytics);
            setAdsConsent(consent.ads);
            setVisible(true);
          }}
          styleName="floating-consent-trigger"
          aria-label="Muuta evästeasetuksia"
          title="Muuta evästeasetuksia"
        >
          <svg viewBox="0 0 24 24" styleName="cookie-icon" fill="currentColor">
            <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm1 16.5c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5 1.5zm-3.5-3c-.552 0-1-.448-1-1s.448-1 1-1 1 .448 1 1-.448 1-1 1zm0-3.5c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5-1.5zm6.5 3c-.552 0-1-.448-1-1s.448-1 1-1 1 .448 1 1-.448 1-1 1zm-1.5-4c-.552 0-1-.448-1-1s.448-1 1-1 1 .448 1 1-.448 1-1 1zm2.5-3c-.552 0-1-.448-1-1s.448-1 1-1 1 .448 1 1-.448 1-1 1z" />
          </svg>
        </button>
      )}
    </>
  );
};

export default CookieConsentBanner;
